import { motion } from "framer-motion";
import { ImagePlus } from "lucide-react";
import { fadeUp, EASE } from "@/lib/motion";

/**
 * ProductCard — a reusable card with an image slot.
 *
 * If `image` is a URL, the photo renders. If `image` is falsy (null/undefined),
 * a beautiful branded placeholder frame renders in the same shape so the
 * layout stays identical when the real photo is added later.
 *
 * Variants:
 *   - size:  "sm" | "md" | "lg"   (image height)
 *   - shape: "rect" | "arch" | "pill"
 *   - tone:  "cream" | "dark"     (background)
 */
export const ProductCard = ({
  name,
  subtitle,
  badge,
  description,
  image,
  size = "md",
  shape = "rect",
  tone = "cream",
  testId,
  className = "",
}) => {
  const heights = {
    sm: "h-56",
    md: "h-72",
    lg: "h-96",
  };
  const shapeCls =
    shape === "arch"
      ? "arch-frame"
      : shape === "pill"
      ? "rounded-full"
      : "rounded-2xl";
  const isDark = tone === "dark";
  const surface = isDark
    ? "border border-[#F0E6C0]/25 text-[#F0E6C0]"
    : "border border-[#3D2817] text-[#3D2817]";
  const subtitleColor = isDark ? "text-[#D8B98A]" : "text-[#7A4E24]";
  const bodyColor = isDark ? "text-[#E6D9A8]/85" : "text-[#6B4423]";

  return (
    <motion.article
      variants={fadeUp}
      data-testid={testId}
      className={`group flex flex-col ${className}`}
    >
      <div className={`overflow-hidden ${shapeCls} ${surface}`}>
        {image ? (
          <div
            className={`${heights[size]} w-full ${
              isDark ? "bg-[#4A3120]" : "bg-[#EFE4B8]"
            } flex items-center justify-center p-3`}
          >
            <motion.img
              src={image}
              alt={name}
              loading="lazy"
              className="max-h-full max-w-full object-contain transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
              initial={{ scale: 1.05, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, ease: EASE }}
            />
          </div>
        ) : (
          <div
            data-testid={testId ? `${testId}-image-slot` : undefined}
            className={`${heights[size]} relative flex w-full items-center justify-center overflow-hidden ${
              isDark ? "bg-[#4A3120]" : "bg-[#EFE4B8]"
            }`}
          >
            {/* Decorative diagonal lines */}
            <div
              className="absolute inset-0 opacity-30"
              style={{
                backgroundImage: isDark
                  ? "repeating-linear-gradient(45deg, transparent 0 14px, rgba(240,230,192,0.08) 14px 15px)"
                  : "repeating-linear-gradient(45deg, transparent 0 14px, rgba(61,40,23,0.08) 14px 15px)",
              }}
            />
            <div className="relative flex flex-col items-center gap-3 text-center">
              <ImagePlus
                size={30}
                strokeWidth={1.3}
                className={isDark ? "text-[#E0A96D]" : "text-[#7A4E24]"}
              />
              <p
                className={`font-display text-2xl leading-none tracking-tight ${
                  isDark ? "text-[#F0E6C0]" : "text-[#3D2817]"
                }`}
              >
                {name}
              </p>
              <p
                className={`text-[10px] uppercase tracking-[0.2em] ${subtitleColor}`}
              >
                Photo slot
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="mt-5 flex flex-col gap-2">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className={`font-display text-2xl md:text-3xl tracking-tight ${isDark ? "text-[#F0E6C0]" : "text-[#3D2817]"}`}>
            {name}
          </h3>
          {badge && (
            <span
              className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.15em] ${
                isDark
                  ? "border-[#E0A96D] text-[#E0A96D]"
                  : "border-[#7A4E24] text-[#7A4E24]"
              }`}
            >
              {badge}
            </span>
          )}
        </div>
        {subtitle && (
          <p className={`text-xs uppercase tracking-[0.18em] ${subtitleColor}`}>
            {subtitle}
          </p>
        )}
        {description && (
          <p className={`mt-1 text-sm md:text-base leading-relaxed ${bodyColor}`}>
            {description}
          </p>
        )}
      </div>
    </motion.article>
  );
};
